import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { AdminPageHeader } from "./_helpers";
import { toast } from "sonner";
import { CheckCircle, XCircle, Images } from "lucide-react";

export default function AdminProvinces() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  // Per-province in-flight save tracker and local override for the limit so
  // the input stays responsive while the request is in flight.
  const [savingId, setSavingId] = useState(null);
  const [localLimits, setLocalLimits] = useState({});

  const load = useCallback(
    () =>
      api.get("/provinces?active_only=false").then((r) => {
        const list = r.data || [];
        setItems(list);
        // Seed local limits from server so blur/debounce comparison is clean.
        setLocalLimits((prev) => {
          const next = { ...prev };
          for (const p of list) {
            if (next[p.id] === undefined) next[p.id] = p.slider_limit ?? 12;
          }
          return next;
        });
      }),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (id) => {
    try {
      await api.post(`/provinces/${id}/toggle`);
      toast.success("Güncellendi");
      load();
    } catch {
      toast.error("Hata");
    }
  };

  // Commit the slider limit for a province. Clamped to a sane range to prevent
  // either accidental "slider shows nothing" or runaway queries.
  const commitLimit = async (p, raw) => {
    const current = p.slider_limit ?? 12;
    let next = parseInt(raw, 10);
    if (!Number.isFinite(next)) next = 12;
    next = Math.min(50, Math.max(1, next));
    if (next === current) {
      setLocalLimits((prev) => ({ ...prev, [p.id]: next }));
      return;
    }
    setSavingId(p.id);
    try {
      await api.put(`/provinces/${p.id}`, { slider_limit: next });
      setItems((prev) =>
        prev.map((x) => (x.id === p.id ? { ...x, slider_limit: next } : x))
      );
      setLocalLimits((prev) => ({ ...prev, [p.id]: next }));
      toast.success(`${p.name}: slider limiti ${next}`);
    } catch (e) {
      toast.error("Kaydedilemedi: " + (e?.response?.data?.detail || e.message));
      // Revert local to server value on failure.
      setLocalLimits((prev) => ({ ...prev, [p.id]: current }));
    } finally {
      setSavingId(null);
    }
  };

  const filtered = items.filter((p) =>
    p.name.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div>
      <AdminPageHeader
        title="İl Yönetimi"
        subtitle={`Toplam ${items.length} il, ${
          items.filter((p) => p.is_active).length
        } aktif · Slider limiti her il için ayrı ayarlanır (varsayılan 12)`}
      />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="İl ara..."
        className="mb-4 px-3 py-2 border border-gray-200 rounded-md text-sm w-72"
        data-testid="admin-prov-search"
      />
      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {filtered.map((p) => {
          const limit = localLimits[p.id] ?? p.slider_limit ?? 12;
          const isSaving = savingId === p.id;
          return (
            <div
              key={p.id}
              className="bg-white border border-gray-100 rounded-md p-3 flex flex-col gap-2"
              data-testid={`admin-prov-${p.slug}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs text-gray-500">{p.plate_number}</div>
                  <div className="font-semibold truncate">{p.name}</div>
                </div>
                <button
                  data-testid={`toggle-${p.slug}`}
                  onClick={() => toggle(p.id)}
                  className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold whitespace-nowrap ${
                    p.is_active
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {p.is_active ? (
                    <>
                      <CheckCircle className="w-3.5 h-3.5" /> Aktif
                    </>
                  ) : (
                    <>
                      <XCircle className="w-3.5 h-3.5" /> Pasif
                    </>
                  )}
                </button>
              </div>

              {/* Slider limit control */}
              <div className="flex items-center gap-2 border-t border-gray-100 pt-2">
                <Images className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <label
                  htmlFor={`sl-${p.id}`}
                  className="text-[11px] text-gray-600 font-medium"
                >
                  Slider limiti
                </label>
                <input
                  id={`sl-${p.id}`}
                  type="number"
                  min={1}
                  max={50}
                  value={limit}
                  disabled={isSaving}
                  onChange={(e) =>
                    setLocalLimits((prev) => ({
                      ...prev,
                      [p.id]: e.target.value,
                    }))
                  }
                  onBlur={(e) => commitLimit(p, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.currentTarget.blur();
                  }}
                  className="w-14 px-2 py-1 border border-gray-200 rounded text-sm text-center focus:border-[#e11d2a] focus:outline-none disabled:opacity-60"
                  data-testid={`admin-prov-slider-limit-${p.slug}`}
                />
                <span className="text-[10px] text-gray-400">haber</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
